import {
	CHART_BAR_ORIENTATION,
	CHART_BAR_ORIENTATION_OPTIONS,
	normalizeChartBarOrientation,
} from '../../shared/lib/barOrientation';
import {
	COMPOSITION_CHART_TYPE,
	COMPOSITION_CHART_TYPE_OPTIONS,
	isCompositionChartBarType,
	isCompositionChartFunnelType,
	isCompositionChartLineType,
	isCompositionChartPieLikeType,
} from '../../composition/lib/compositionChartConfig';

export const OVERDUE_CHART_RANGE_MODE = {
	ALL: 'all',
	OVERDUE_ONLY: 'overdue-only',
};

export const OVERDUE_CHART_RANGE_MODE_OPTIONS = [
	{ value: OVERDUE_CHART_RANGE_MODE.ALL, label: '全部区间' },
	{ value: OVERDUE_CHART_RANGE_MODE.OVERDUE_ONLY, label: '仅超期' },
];

export const OVERDUE_CHART_DIMENSION = {
	BUCKET: 'bucket',
	PAYMENT_TYPE: 'paymentType',
	CUSTOMER: 'customer',
	SALE_USER: 'saleUser',
};

export const OVERDUE_CHART_METRIC = {
	AR_AMOUNT: 'arAmount',
	ORDER_COUNT: 'orderCount',
};

export const OVERDUE_CHART_DIMENSION_OPTIONS = [
	{ value: OVERDUE_CHART_DIMENSION.BUCKET, label: '账龄区间' },
	{ value: OVERDUE_CHART_DIMENSION.PAYMENT_TYPE, label: '尾款类型' },
	{ value: OVERDUE_CHART_DIMENSION.CUSTOMER, label: '客户' },
	{ value: OVERDUE_CHART_DIMENSION.SALE_USER, label: '销售员' },
];

export const OVERDUE_CHART_METRIC_OPTIONS = [
	{ value: OVERDUE_CHART_METRIC.AR_AMOUNT, label: '应收账款' },
	{ value: OVERDUE_CHART_METRIC.ORDER_COUNT, label: '单数' },
];

export const OVERDUE_CHART_BAR_DEFAULT_ORIENTATION = CHART_BAR_ORIENTATION.VERTICAL;
export const OVERDUE_CHART_CUSTOMER_BAR_ORIENTATION = CHART_BAR_ORIENTATION.HORIZONTAL;

export const OVERDUE_CHART_TYPE_OPTIONS = COMPOSITION_CHART_TYPE_OPTIONS;

export const DEFAULT_OVERDUE_CHART_CONFIG = {
	rangeMode: OVERDUE_CHART_RANGE_MODE.ALL,
	dimension: OVERDUE_CHART_DIMENSION.BUCKET,
	metric: OVERDUE_CHART_METRIC.AR_AMOUNT,
	chartType: COMPOSITION_CHART_TYPE.BAR,
	barOrientation: OVERDUE_CHART_BAR_DEFAULT_ORIENTATION,
};

/** 经营看板右侧超期排行榜默认口径 */
export const DASHBOARD_OVERDUE_RANK_CHART_CONFIG = {
	rangeMode: OVERDUE_CHART_RANGE_MODE.OVERDUE_ONLY,
	dimension: OVERDUE_CHART_DIMENSION.CUSTOMER,
	metric: OVERDUE_CHART_METRIC.AR_AMOUNT,
	chartType: COMPOSITION_CHART_TYPE.BAR,
	barOrientation: OVERDUE_CHART_CUSTOMER_BAR_ORIENTATION,
};

export const DASHBOARD_BUILTIN_OVERDUE_TEMPLATE_ID = 'preset-overdue';

/** 经营看板 builtin 超期排行种子，或自该种子固定到首页的图 */
export const isBuiltinOverdueRankSeedSnapshot = (snapshot = {}) => {
	if (snapshot?.chartKind && snapshot.chartKind !== 'overdue') return false;
	return (
		snapshot?.templateId === DASHBOARD_BUILTIN_OVERDUE_TEMPLATE_ID ||
		snapshot?.source === 'builtin' ||
		snapshot?.sourceStatisticsChartId === DASHBOARD_BUILTIN_OVERDUE_TEMPLATE_ID ||
		snapshot?.id === DASHBOARD_BUILTIN_OVERDUE_TEMPLATE_ID
	);
};

export const DASHBOARD_OVERDUE_RANK_DIMENSION_OPTIONS = [
	{ value: OVERDUE_CHART_DIMENSION.CUSTOMER, label: '客户' },
	{ value: OVERDUE_CHART_DIMENSION.SALE_USER, label: '销售员' },
];

const isValidRangeMode = (value) => OVERDUE_CHART_RANGE_MODE_OPTIONS.some((item) => item.value === value);
const isValidDimension = (value) => OVERDUE_CHART_DIMENSION_OPTIONS.some((item) => item.value === value);
const isValidMetric = (value) => OVERDUE_CHART_METRIC_OPTIONS.some((item) => item.value === value);
const isValidChartType = (value) => OVERDUE_CHART_TYPE_OPTIONS.some((item) => item.value === value);

const usesHorizontalRankBar = (dimension) =>
	dimension === OVERDUE_CHART_DIMENSION.CUSTOMER || dimension === OVERDUE_CHART_DIMENSION.SALE_USER;

const resolveBarOrientation = (config) => {
	const fallback = usesHorizontalRankBar(config.dimension)
		? OVERDUE_CHART_CUSTOMER_BAR_ORIENTATION
		: OVERDUE_CHART_BAR_DEFAULT_ORIENTATION;
	return normalizeChartBarOrientation(config.barOrientation, fallback);
};

export const normalizeOverdueChartConfig = (config = {}) => ({
	rangeMode: isValidRangeMode(config.rangeMode) ? config.rangeMode : DEFAULT_OVERDUE_CHART_CONFIG.rangeMode,
	dimension: isValidDimension(config.dimension) ? config.dimension : DEFAULT_OVERDUE_CHART_CONFIG.dimension,
	metric: isValidMetric(config.metric) ? config.metric : DEFAULT_OVERDUE_CHART_CONFIG.metric,
	chartType: isValidChartType(config.chartType) ? config.chartType : DEFAULT_OVERDUE_CHART_CONFIG.chartType,
	barOrientation: resolveBarOrientation(config),
});

export const getOverdueChartRangeModeLabel = (rangeMode) =>
	OVERDUE_CHART_RANGE_MODE_OPTIONS.find((item) => item.value === rangeMode)?.label ?? '全部区间';

export const getOverdueChartDimensionLabel = (dimension) =>
	OVERDUE_CHART_DIMENSION_OPTIONS.find((item) => item.value === dimension)?.label ?? '账龄区间';

export const getOverdueChartMetricLabel = (metric) =>
	OVERDUE_CHART_METRIC_OPTIONS.find((item) => item.value === metric)?.label ?? '应收账款';

export const getOverdueChartTypeLabel = (chartType) =>
	OVERDUE_CHART_TYPE_OPTIONS.find((item) => item.value === chartType)?.label ?? '柱状';

export const isOverdueChartBarType = isCompositionChartBarType;
export const isOverdueChartPieLikeType = isCompositionChartPieLikeType;
export const isOverdueChartFunnelType = isCompositionChartFunnelType;
export const isOverdueChartLineType = isCompositionChartLineType;

export { CHART_BAR_ORIENTATION_OPTIONS, COMPOSITION_CHART_TYPE };
